<template>
    <div>
        <loader v-if="isLoading" data-type="DirectedTreeLoader" class="custom-loading mt-4" />
        <div id="chartdiv" class="chartdiv"></div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as am5 from "@amcharts/amcharts5";
// eslint-disable-next-line camelcase
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import Director from '@/models/Director';
import Shareholder from '@/models/Shareholder';
import CompanySecretary from '@/models/CompanySecretary';
import CompanyNomineeDirector from '@/models/CompanyNomineeDirector';
import CompanyAuditor from '@/models/CompanyAuditor';

export default {
    data() {
        return {
            isLoading: true
        };
    },

    computed: {
        ...mapGetters({
            fullCompanyName: "company/fullCompanyName",
        }),
    },

    async mounted() {
        this.directors = await this.getDirectorsName();
        this.shareholders = await this.getShareholdersName();
        this.nomineeDirectors = await this.getNomineeDirectorsName();
        this.secretaries = await this.getSecretariesName();
        this.auditors = await this.getAuditorsName();

        this.isLoading = false;

        this.render();
    },

    methods: {
        async getDirectorsName()
        {
            const directors = await Director.all();
           
            // filter active director
            const activeDirectors = directors.filter(director => !director.cessation_date);

            return activeDirectors.map(director => director.user?.user_info?.legal_name ?? director.user.name);
        },

        async getShareholdersName()
        {
            const { shareholders } = await Shareholder.all();

            return shareholders.map(shareholder => shareholder.user?.user_info?.legal_name ?? shareholder.user.name);
        },

        async getNomineeDirectorsName()
        {
            const nomineeDirectors = await CompanyNomineeDirector.all();

            // filter active nominee director
            const activeNomineeDirectors = nomineeDirectors.filter(director => !director.cessation_date);

            return activeNomineeDirectors.map(nomineeDirector => nomineeDirector.user?.user_info?.legal_name ?? nomineeDirector.user.name);
        },

        async getSecretariesName()
        {
            const secretaries = await CompanySecretary.all();

            // filter active secretaries
            const activeSecretaries = secretaries.filter(director => !director.cessation_date);

            return activeSecretaries.map(secretary => secretary.user?.user_info?.legal_name ?? secretary.user.name);
        },

        async getAuditorsName()
        {
            const { auditors } = await CompanyAuditor.all();

            // filter active auditors
            const activeAuditors = auditors.filter(director => !director.cessation_date);

            return activeAuditors.map(auditor => auditor.auditor.name);
        },

        getContents() {
            const titles = [
                {key: 'directors', value: 'directors', color: 0x095256},
                {key: 'shareholders', value: 'shareholders', color: 0x087f8c},
                {key: 'nomineeDirectors', value: 'nominee directors', color: 0x5aaa95},
                {key: 'secretaries', value: 'secretaries', color: 0x86a873},
                {key: 'auditors', value: 'auditors'},
            ];

            const companyData = [];

            titles.forEach(title => {
                const names = this[`${title.key}`];

                const children = [];
                names.forEach(name => {
                    children.push({
                        name,
                        value: 1,
                        nodeSettings: title.color ? {
                            fill: am5.color(title.color),
                        } : {},
                    });
                });

                companyData.push({
                    name: title.value.toUpperCase(),
                    nodeSettings: title.color ? {
                        fill: am5.color(title.color),
                    } : {},
                    value: 2,
                    children
                });
            });
 
            return [
                {
                    name: this.fullCompanyName,
                    x: am5.percent(45),
                    y: am5.percent(40),
                    value: 4,
                    nodeSettings: {
                        fill: am5.color(0x4267b2),
                    },
                    children: companyData
                },
            ];
        },

        render() {
            // Create root and chart
            const root = am5.Root.new("chartdiv");

            root.setThemes([
                am5themes_Animated.new(root),
            ]);

            const container = root.container.children.push(am5.Container.new(root, {
                width: am5.percent(100),
                height: am5.percent(100),
                layout: root.verticalLayout
            }));

            const series = container.children.push(am5hierarchy.ForceDirected.new(root, {
                downDepth: 1,
                initialDepth: 2,
                topDepth: 1,
                valueField: "value",
                categoryField: "name",
                childDataField: "children",
                nodePadding: 25,
                xField: "x",
                yField: "y",
                minRadius: 12,
                maxRadius: am5.percent(10),
                manyBodyStrength: -30,
            }));

            series.circles.template.setAll({
                templateField: "nodeSettings",
            });

            series.nodes.template.set("tooltipText", "{category}");

            const contents = this.getContents();

            series.data.setAll([{
                name: "Root",
                value: 0,
                children: contents
            }]);

            series.set("selectedDataItem", series.dataItems[0]);
        }
    },
};
</script>

<style scoped>
#chartdiv {
  width: 100%;
  height: 190vh;
  max-width: 100%;
  margin: auto;
}
</style>